import { type ScopeChangeRequestStore } from '@/features/admin/scope-change-requests/ScopeChangeRequestStore'
import {
  ScopeChangeRequest,
  type ScopeChangeRequestItem,
} from '@/features/admin/scope-change-requests/ScopeChangeRequest'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, observable } from 'mobx'
import { task } from 'mobx-task'
import { validateElements } from '@/utils/validx-util'
import { validationContext } from 'validx'
import { randomUUID } from '@/utils/uuid-util'
import {
  AddInventoryItemsToEditDialogViewModel,
  type EditScopeChangeInventoryItemToAddViewModel,
} from '@/features/admin/scope-change-requests/AddInventoryItemsToEditDialog/AddInventoryItemsToEditDialogViewModel'
import { type ListInventoryItemsFn } from '@/features/inventory'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { Id } from '@/infra/id'

export class ScopeChangeEditInventoryItemsDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly modal = new ModalViewModel()

  @observable
  accessor scopeChangeRequest = ScopeChangeRequest.none

  @observable
  accessor editScopeChangeInventoryItems: EditScopeChangeInventoryItemViewModel[] =
    []

  readonly scopeChangesValidationStep = validationContext(this, {
    editScopeChangeInventoryItems: [
      validateElements('Some scope changes are invalid.'),
    ],
  })

  readonly addInventoryItemsToEditDialogVm

  constructor(
    private readonly _searchInventoryItems: ListInventoryItemsFn,
    private readonly _scopeChangeRequestStore: ScopeChangeRequestStore,
    private readonly _onSave: () => Promise<void>,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this.addInventoryItemsToEditDialogVm =
      new AddInventoryItemsToEditDialogViewModel(
        this._searchInventoryItems,
        createAlertRetryWrapper,
        this.handleItemsAdded,
      )

    this._lifecycle.manage(this.addInventoryItemsToEditDialogVm)
  }

  /**
   * Adds items to the inventory items to edit
   */
  @action.bound
  handleItemsAdded(itemsToAdd: EditScopeChangeInventoryItemToAddViewModel[]) {
    const editItems = itemsToAdd
      .filter(
        (item) =>
          !this.editScopeChangeInventoryItems
            .map((i) => i.versionId)
            .includes(item.versionId),
      )
      .map((item) => {
        const previousItem = this.scopeChangeRequest.job.jobScopeItems.find(
          (c) => c.inventoryItemId === item.itemId,
        )

        const versionId = previousItem?.inventoryItemVersionId ?? item.versionId
        const name = previousItem?.name ?? item.name
        const currentQuantity = previousItem?.quantity ?? 0
        const newQuantity = previousItem?.quantity ?? 1

        return new EditScopeChangeInventoryItemViewModel({
          versionId,
          name,
          newQuantity,
          currentQuantity,
        })
      })
    this.editScopeChangeInventoryItems =
      this.editScopeChangeInventoryItems.concat(editItems)
  }

  /**
   * Opens the dialog
   * @param scopeChangeRequest
   */
  @action.bound
  open(scopeChangeRequest: ScopeChangeRequest) {
    this.scopeChangeRequest = scopeChangeRequest
    this.editScopeChangeInventoryItems = scopeChangeRequest.inventoryItems.map(
      EditScopeChangeInventoryItemViewModel.fromInventoryScopeChangeItem,
    )
    this.modal.show()
  }

  /**
   * Edit the scope change request and close the dialog
   */
  save = task.resolved(async () => {
    if (!this.scopeChangesValidationStep.validate().isValid) {
      return
    }

    await this._scopeChangeRequestStore
      .editScopeChangeRequest({
        scopeChangeRequestId: this.scopeChangeRequest.id,
        customItems: this.scopeChangeRequest.customItems.map((i) => ({
          name: i.name,
          quantity: i.quantity,
          price: i.price,
          proPrice: i.proPrice,
        })),
        inventoryItems: this.editScopeChangeInventoryItems.map((i) => ({
          versionId: i.versionId,
          quantity: i.newQuantity,
        })),
      })
      .then(() =>
        this._scopeChangeRequestStore.fetchScopeChangeRequest(
          this.scopeChangeRequest.id,
          this._lifecycle.signal,
        ),
      )
      .catch(this._createAlertErrorHandler('Updating the items failed'))

    this._newAlert()
      .withState('success')
      .saying('Success', 'Scope change items updated')
      .autoDismiss(2000)
      .show()

    void this._onSave()
    this.modal.close()
  })

  /**
   * Opens the dialog for adding service items.
   */
  @action.bound
  showAddItems() {
    this.addInventoryItemsToEditDialogVm.open(this.scopeChangeRequest)
  }

  /**
   * Removes a previously added service item change.
   *
   * @param versionId
   */
  @action.bound
  removeScopeChange(versionId: Id) {
    const pos = this.editScopeChangeInventoryItems.findIndex(
      (item) => item.versionId === versionId,
    )
    this.editScopeChangeInventoryItems.splice(pos, 1)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }
}

/**
 * Initial values of an edited service item change.
 */
export interface EditScopeChangeInventoryItemViewModelProps {
  versionId: Id
  name: string
  newQuantity: number
  currentQuantity: number
}

export class EditScopeChangeInventoryItemViewModel {
  readonly key = randomUUID()

  @observable accessor versionId = Id.none

  @observable accessor name = ''

  /**
   * The new quantity of the item
   */
  @observable accessor newQuantity = 1

  /**
   * The current quantity of the item
   */
  @observable accessor currentQuantity = 0

  /**
   * Validation context for custom scope change.
   */
  readonly validation = validationContext(this, {
    newQuantity: [
      (opts) => opts.value >= 0 || 'Quantity cannot be less than zero.',
    ],
  })

  constructor({
    versionId,
    name,
    newQuantity,
    currentQuantity,
  }: EditScopeChangeInventoryItemViewModelProps) {
    this.versionId = versionId
    this.name = name
    this.newQuantity = newQuantity
    this.currentQuantity = currentQuantity
  }

  @action.bound
  setQuantity(value: number | null) {
    this.newQuantity = value === null || value === 0 ? 1 : value
  }

  @action.bound
  incrementQuantity() {
    this.newQuantity += 1
  }

  @action.bound
  decrementQuantity() {
    if (this.newQuantity === 0) {
      return
    }

    this.newQuantity -= 1
  }

  static fromInventoryScopeChangeItem(item: ScopeChangeRequestItem) {
    return new EditScopeChangeInventoryItemViewModel({
      versionId: item.inventoryItemVersionId,
      name: item.name,
      newQuantity: item.quantity,
      currentQuantity: item.quantity,
    })
  }
}
